import { DEFAULT_LOCALE, DEFAULT_THEME_CONFIG, mergeThemeConfig, normalizePreference } from '@/utils/userPreference.mjs';
import { FIXED_TABLE_PAGE_BACKGROUND } from '@/utils/themePresets.mjs';

const publicTableThemeFields = Object.keys(DEFAULT_THEME_CONFIG.tableTheme);

function getThemeConfig(state) {
    return {
        themeColor: state.themeColor,
        buttonColor: state.buttonColor,
        useIndependentButtonColor: state.useIndependentButtonColor,
        formLabelColor: state.formLabelColor,
        tableTheme: Object.fromEntries(publicTableThemeFields.map((key) => [key, state.tableTheme[key]])),
    };
}

function applyThemeConfig(state, themeConfig) {
    const nextTheme = mergeThemeConfig(themeConfig);
    state.themeColor = nextTheme.themeColor;
    state.useIndependentButtonColor = Boolean(nextTheme.useIndependentButtonColor);
    state.buttonColor = state.useIndependentButtonColor ? nextTheme.buttonColor : nextTheme.themeColor;
    state.formLabelColor = nextTheme.formLabelColor;
    state.tableTheme = { ...state.tableTheme, ...nextTheme.tableTheme };
}

export default {
    state: {
        appNameCh: "动物饲养管理平台",
        appNameEng: "Animal Breeding And Management Platform",
        // 主题色：用于标题字体、菜单激活等
        themeColor: DEFAULT_THEME_CONFIG.themeColor,
        // 表单 Label 颜色
        formLabelColor: DEFAULT_THEME_CONFIG.formLabelColor,
        // 按钮颜色 (通常指 primary 按钮)
        buttonColor: DEFAULT_THEME_CONFIG.buttonColor,
        // 是否允许按钮颜色独立于品牌主色
        useIndependentButtonColor: false,
        // 当前语言：zh-CN 为简体中文，zh-HK 为香港繁体
        locale: DEFAULT_LOCALE,
        // 表格主题：通过 App.vue 转换为 CSS 变量，供 Ag Grid 与 PrimeVue TreeTable 共用。
        tableTheme: {
            // 表格页面背景色
            pageBackground: DEFAULT_THEME_CONFIG.tableTheme.pageBackground,
            // 表格容器背景色
            surfaceBackground: DEFAULT_THEME_CONFIG.tableTheme.surfaceBackground,
            // 表头背景色
            headerBackground: DEFAULT_THEME_CONFIG.tableTheme.headerBackground,
            // 表头文字颜色
            headerTextColor: DEFAULT_THEME_CONFIG.tableTheme.headerTextColor,
            // 表头字体族
            headerFontFamily: "HarmonyOS_Font_Bold, sans-serif",
            // 表头字体大小
            headerFontSize: "14px",
            // 表头字体粗细
            headerFontWeight: "600",
            // 表头行高
            headerLineHeight: "44px",
            // 表体背景色
            bodyBackground: DEFAULT_THEME_CONFIG.tableTheme.bodyBackground,
            // 表体文字颜色
            bodyTextColor: DEFAULT_THEME_CONFIG.tableTheme.bodyTextColor,
            // 表体字体族
            bodyFontFamily: "HarmonyOS_Font_Regular, sans-serif",
            // 表体字体大小
            bodyFontSize: "13px",
            // 表体行高
            bodyLineHeight: "40px",
            // 表格边框颜色
            borderColor: DEFAULT_THEME_CONFIG.tableTheme.borderColor,
            // 表格边框宽度
            borderWidth: "1px",
            // 行悬停背景色
            rowHoverBackground: DEFAULT_THEME_CONFIG.tableTheme.rowHoverBackground,
            // 斑马纹行背景色
            rowStripeBackground: DEFAULT_THEME_CONFIG.tableTheme.rowStripeBackground,
            // 滚动条轨道颜色
            scrollbarTrackColor: DEFAULT_THEME_CONFIG.tableTheme.scrollbarTrackColor,
            // 滚动条滑块颜色
            scrollbarThumbColor: DEFAULT_THEME_CONFIG.tableTheme.scrollbarThumbColor,
            // 滚动条滑块悬停颜色
            scrollbarThumbHoverColor: DEFAULT_THEME_CONFIG.tableTheme.scrollbarThumbHoverColor,
            // 滚动条尺寸
            scrollbarSize: "12px",
        }
    },
    getters: {
        userPreference: (state) => ({ locale: state.locale, themeConfig: getThemeConfig(state) }),
    },
    actions: {},
    mutations: {
        setThemeColor(state, color) {
            state.themeColor = color;
            if (!state.useIndependentButtonColor) state.buttonColor = color;
        },
        setFormLabelColor(state, color) {
            state.formLabelColor = color;
        },
        setButtonColor(state, color) {
            state.buttonColor = color;
        },
        setUseIndependentButtonColor(state, value) {
            state.useIndependentButtonColor = Boolean(value);
            if (!state.useIndependentButtonColor) state.buttonColor = state.themeColor;
        },
        setTableTheme(state, tableTheme) {
            state.tableTheme = {
                ...state.tableTheme,
                ...tableTheme,
                pageBackground: FIXED_TABLE_PAGE_BACKGROUND,
            };
        },
        applyThemeConfig(state, themeConfig) {
            applyThemeConfig(state, themeConfig);
        },
        resetThemeConfig(state) {
            applyThemeConfig(state, DEFAULT_THEME_CONFIG);
        },
        setLocale(state, locale) {
            state.locale = locale === 'zh-HK' ? 'zh-HK' : DEFAULT_LOCALE;
        },
        setUserPreference(state, preference) {
            const normalized = normalizePreference(preference);
            state.locale = normalized.locale;
            applyThemeConfig(state, normalized.themeConfig);
        },
    }
}
